import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import InputTextArea from "../../components/staff/InputTextArea";
import Cancel from "../../components/Atomos/Icons/Close";
import Success from "../../components/Atomos/Icons/Success";
import IFirm from "../../interfaces/models/IFirm";
import { getFirmById, updateFirm } from "../../Services/Firm";

type FirmDescriptionModalProps = {
  isOpen: boolean;
  firmId: string;
  firmName: string;
  onClose: () => void;
};

type ModalMode = "view" | "edit";

const FirmDescriptionModal: React.FC<FirmDescriptionModalProps> = ({
  isOpen,
  firmId,
  firmName,
  onClose,
}) => {
  const [mode, setMode] = useState<ModalMode>("view");
  const [firm, setFirm] = useState<IFirm | null>(null);
  const [draftOverview, setDraftOverview] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setMode("view");
    setFirm(null);
    setDraftOverview("");
    setLoading(false);
    setSaving(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen || !firmId.trim()) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setMode("view");

    void getFirmById(firmId.trim())
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        if (!loaded) {
          setError("Could not load firm description.");
          setFirm(null);
          setDraftOverview("");
          return;
        }
        setFirm(loaded);
        const overview = String(loaded.firmOverview ?? "").trim();
        setDraftOverview(overview);
        setMode(overview ? "view" : "edit");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load firm description.");
          setFirm(null);
          setDraftOverview("");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, firmId]);

  const handleEdit = () => {
    setDraftOverview(String(firm?.firmOverview ?? "").trim());
    setMode("edit");
    setError(null);
  };

  const handleCancelEdit = () => {
    const savedOverview = String(firm?.firmOverview ?? "").trim();
    if (!savedOverview) {
      handleClose();
      return;
    }
    setDraftOverview(savedOverview);
    setMode("view");
    setError(null);
  };

  const handleSave = async () => {
    if (!firm) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await updateFirm({
        ...firm,
        firmOverview: draftOverview.trim(),
      });
      setFirm(updated);
      const savedOverview = String(updated.firmOverview ?? "").trim();
      setDraftOverview(savedOverview);
      setMode(savedOverview ? "view" : "edit");
    } catch {
      setError("Could not save firm description.");
    } finally {
      setSaving(false);
    }
  };

  const displayOverview = String(firm?.firmOverview ?? "").trim();
  const title = firmName.trim()
    ? `Firm Description — ${firmName.trim()}`
    : "Firm Description";

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title={title}
      compact
      compactBodyScrolls={mode === "edit"}
      className="max-w-2xl w-full"
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <p className="text-gray-font text-base">Loading…</p>
        ) : error && !firm ? (
          <p className="text-base text-red-600">{error}</p>
        ) : mode === "view" ? (
          <div className="min-h-[120px] whitespace-pre-wrap text-base leading-6 text-gray-font">
            {displayOverview || (
              <span className="text-gray-500 italic">No description yet.</span>
            )}
          </div>
        ) : (
          <InputTextArea
            title="Firm Overview"
            value={draftOverview}
            onChange={setDraftOverview}
            maxChars={3500}
            showCharacterCount
            rows={8}
            minHeightClassName="min-h-[200px]"
            disabled={saving}
          />
        )}

        {error && firm ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : null}

        {!loading && firm ? (
          <div className="mt-2 flex flex-wrap justify-end gap-3 border-t border-neutral-200 pt-4">
            {mode === "view" ? (
              <>
                <LegacyButton
                  text="Close"
                  style="outlined"
                  size="medium"
                  content="text"
                  onClick={handleClose}
                  color="neutral"
                  className="!w-auto shrink-0"
                />
                <LegacyButton
                  text="Edit"
                  style="filled"
                  size="medium"
                  content="text"
                  onClick={handleEdit}
                  color="accent"
                  className="!w-auto shrink-0"
                />
              </>
            ) : (
              <>
                <LegacyButton
                  text="Cancel"
                  icon={<Cancel />}
                  style="outlined"
                  size="medium"
                  content="textIcon"
                  onClick={handleCancelEdit}
                  color="neutral"
                  disabled={saving}
                  className="!w-auto shrink-0"
                />
                <LegacyButton
                  text="Save"
                  icon={<Success />}
                  style="filled"
                  size="medium"
                  content="textIcon"
                  onClick={() => void handleSave()}
                  color="accent"
                  disabled={saving}
                  isLoading={saving}
                  className="!w-auto shrink-0"
                />
              </>
            )}
          </div>
        ) : !loading && !firm ? (
          <div className="mt-2 flex justify-end border-t border-neutral-200 pt-4">
            <LegacyButton
              text="Close"
              style="outlined"
              size="medium"
              content="text"
              onClick={handleClose}
              color="neutral"
              className="!w-auto shrink-0"
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default FirmDescriptionModal;
